import type { ReactNode } from 'react';
import { ButtonGroup, type ButtonGroupProps } from '~/components/new/Button';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';

export const formGapSizes = {
    xsmall: '1',
    small: '2',
    medium: '4',
    large: '6',
    xlarge: '8',
    none: '0',
} as const;

// Initial box with top/bottom margins and xlarge gaps.
const FormLayout = ({
    children,
    contentWidthCols = 6,
    className = '',
    ['data-test-component']: testComponent = 'layout/templates/FormLayout',
}: {
    children: ReactNode;
    contentWidthCols?: number;
    className?: string;
} & DataTestAttributes) => (
    <div
        className={classNames({ 'layout form': true, [className]: Boolean(className) })}
        data-test-component={testComponent}>
        <div
            className={classNames({
                'form-wrapper': true,
                [`max-w-${contentWidthCols}col`]: true,
            })}>
            {children}
        </div>
    </div>
);

// 2nd order group for fields, configurable direction and gap
const LayoutGroup = ({
    children,
    gap = 'large',
    direction = 'column',
    ['data-test-component']: testComponent = 'layout/templates/FormLayout/LayoutGroup',
    ['data-test-name']: testName,
    ['data-test-id']: testId,
}: {
    children: ReactNode;
    gap?: keyof typeof formGapSizes;
    direction?: 'column' | 'row';
} & DataTestAttributes) => (
    <div
        className={classNames({
            [`layout-form-group form-group gap-${formGapSizes[gap]}`]: true,
            'layout-form-group-row': direction === 'row',
        })}
        data-test-component={testComponent}
        data-test-name={testName}
        data-test-id={testId}>
        {children}
    </div>
);

// group for buttons aligned to sides.
const Buttons = ({
    align = 'sides',
    children,
}: {
    align?: ButtonGroupProps['align'];
    children: ReactNode;
}) => <ButtonGroup align={align}>{children}</ButtonGroup>;

FormLayout.Group = LayoutGroup;
FormLayout.Buttons = Buttons;

export default FormLayout;
